import { PhonePlaceSwitch, usePlaceSwitch } from "../account/places.tsx";
import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { signOut } from "@teamna/web-client/auth/auth-store.ts";
import { Button } from "./Button.tsx";
import { ArrowLeftIcon, SignOutIcon } from "./Icons.tsx";
import { Sheet } from "./Sheet.tsx";

/**
 * The frame every screen sits in.
 *
 * One 430px column, centred, with the page gutter at 20px so a card is 350px wide on a 390px
 * phone. `pb-tabbar` reserves the height of the fixed bar plus the home indicator, so the last card
 * in a list is always reachable rather than hiding a few pixels behind the bar.
 *
 * The header is not sticky. On a phone a sticky header eats a fifth of the viewport for a word you
 * have already read; the schedule's day headers stick instead, which is the thing worth keeping in
 * view while scrolling.
 */
export function Screen({
  title,
  eyebrow,
  eyebrowMark,
  action,
  back,
  children,
}: {
  title: string;
  eyebrow?: string;
  /** A small picture before the eyebrow: the business mark on the member home. */
  eyebrowMark?: ReactNode;
  action?: ReactNode;
  /** Where the screen above this one lives, for a detail pushed on top of a tab. */
  back?: { to: string; label: string; params?: Record<string, string> };
  children: ReactNode;
}) {
  const places = usePlaceSwitch();
  // Top screens only: a detail pushed on top has its back link in that corner.
  const switchable = !back && places !== null && places.places.length > 1;
  return (
    <div className="pb-tabbar mx-auto w-full max-w-app px-5">
      <header className="pt-safe">
        {back ? (
          <Link
            to={back.to}
            params={back.params}
            className="-ml-1 mt-5 inline-flex min-h-11 items-center gap-1.5 pr-2 text-[14px] font-semibold text-accent-strong"
          >
            <ArrowLeftIcon size={18} />
            {back.label}
          </Link>
        ) : null}
        <div className={`pb-5 ${back ? "pt-1" : switchable ? "pt-4" : "pt-6"}`}>
          {/* An account with two or more places (owner and parent): "Switch" on the eyebrow's line. */}
          {switchable ? (
            <div className="mb-2 flex min-w-0 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                {eyebrow && eyebrowMark ? eyebrowMark : null}
                {eyebrow ? <p className="eyebrow min-w-0 truncate">{eyebrow}</p> : null}
              </div>
              <PhonePlaceSwitch />
            </div>
          ) : eyebrow && eyebrowMark ? (
            <div className="mb-2 flex min-w-0 items-center gap-2">
              {eyebrowMark}
              <p className="eyebrow min-w-0 truncate">{eyebrow}</p>
            </div>
          ) : eyebrow ? (
            <p className="eyebrow mb-1.5 truncate">{eyebrow}</p>
          ) : null}
          {/* The action sits on the title's line, centred on it, not up on the eyebrow. */}
          <div className="flex items-center justify-between gap-3">
            <h1 className="min-w-0 text-[30px]">{title}</h1>
            {action ? <div className="shrink-0">{action}</div> : null}
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

/**
 * Sign out, as a labelled row with a confirmation behind it.
 *
 * This used to be an unlabelled door icon in the top-right corner of every top-level screen, in
 * the spot a phone app puts a profile or a settings control, and it signed you straight out on one
 * tap. A pilot owner pressed it without knowing what it was and lost their session mid-task. So:
 * one place instead of five, the word rather than a glyph, and a second tap before anything
 * happens. Signing out is rare and irreversible; the cost of the extra tap is nothing next to the
 * cost of an accidental one.
 *
 * It lives at the bottom of Profile for a member and of More for an owner, which is where the rest
 * of the account controls already are.
 */
export function SignOutRow({
  onSignOut = signOut,
  subtitle = "To sign in again, you need your mobile number or your email.",
  iconOnly = false,
}: {
  /** What "Yes, sign out" does. Desk clears its token; Control Center ends its Google session. */
  onSignOut?: () => void;
  subtitle?: string;
  /** Only the icon, for the collapsed sidebar. The button is still named "Sign out". */
  iconOnly?: boolean;
} = {}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        aria-label={iconOnly ? "Sign out" : undefined}
        title={iconOnly ? "Sign out" : undefined}
        className={[
          "sk-btn-alt flex min-h-12 w-full items-center justify-center gap-2 rounded-lg text-[15px] font-medium",
          iconOnly ? "px-0" : "px-4",
        ].join(" ")}
      >
        <SignOutIcon size={18} />
        {iconOnly ? null : "Sign out"}
      </button>

      {confirming ? (
        <Sheet
          open
          onClose={() => setConfirming(false)}
          title="Sign out?"
          subtitle={subtitle}
          actions={
            <>
              <Button variant="danger" size="lg" fullWidth onClick={onSignOut}>
                Yes, sign out
              </Button>
              <Button variant="secondary" fullWidth onClick={() => setConfirming(false)}>
                Stay signed in
              </Button>
            </>
          }
        />
      ) : null}
    </>
  );
}

/**
 * A tab that exists so the bar navigates but whose screen has not been built yet.
 *
 * Kept honest on purpose: it says what will be here rather than pretending to be an empty state, so
 * nobody demoing the app mistakes unbuilt for broken.
 */
export function StubScreen({
  title,
  eyebrow,
  summary,
  next,
}: {
  title: string;
  eyebrow?: string;
  summary: string;
  next: string[];
}) {
  return (
    <Screen title={title} eyebrow={eyebrow}>
      <div className="rounded-card border border-line bg-paper p-5 shadow-card">
        <p className="text-[15px] text-ink-2">{summary}</p>
        <p className="eyebrow mt-5 mb-2.5">Coming next</p>
        <ul className="flex flex-col gap-2">
          {next.map((item) => (
            <li key={item} className="flex gap-2.5 text-[14px] text-ink-2">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-accent-soft" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </Screen>
  );
}
